
import React, { useEffect, useState } from "react";
import { getWordForDay } from "@/lib/wordLibrary";
import { findNewLetterIndex, getNewLetter } from "@/lib/types/WordSequence";

// Get example words different from the actual game
const getExampleSequence = () => {
  // Create a consistently different seed for each user
  const userRandomSeed = parseInt(localStorage.getItem('babblegram_user_seed') || '0');
  const seedBase = userRandomSeed || Math.floor(Math.random() * 1000);
  
  // Store the seed for consistency if not already stored
  if (!userRandomSeed) {
    localStorage.setItem('babblegram_user_seed', seedBase.toString());
  }
  
  // Get a sequence from a future date to ensure it's different
  const randomDate = new Date();
  randomDate.setFullYear(randomDate.getFullYear() + 1);
  randomDate.setDate(randomDate.getDate() + (20 + (seedBase % 30)));
  
  const exampleSequence = getWordForDay(randomDate);
  return exampleSequence.slice(0, 4); // Only use the first 4 words
};

export const LevelExampleSection = () => {
  const [exampleWords, setExampleWords] = useState<string[]>([]);
  
  useEffect(() => {
    setExampleWords(getExampleSequence());
  }, []);
  
  if (exampleWords.length < 4) {
    // Fallback if we don't have enough example words
    return <div className="text-center text-gray-400 py-2">Loading examples...</div>;
  }

  const LevelExample = ({ level, word, newLetterIndex = -1, newLetter = "" }) => {
    const letterColor = level === 1 ? "text-[#de0046]" : 
                        level === 2 ? "text-[#8B5CF6]" :
                        level === 3 ? "text-[#0EA5E9]" :
                        "text-[#22C55E]";
    
    return (
      <div className="flex items-center gap-2 mb-2 last:mb-0">
        <div className={`${letterColor} font-bold min-w-[60px]`}>Level {level}</div>
        <div className="flex">
          {word.split('').map((letter, i) => (
            <div 
              key={i}
              className="w-7 h-7 bg-success rounded flex items-center justify-center text-white font-bold text-xs mx-0.5"
              style={i === newLetterIndex ? {
                border: '2px solid #de0046',
                boxShadow: '0 0 3px #de0046'
              } : undefined}
            >
              {letter.toUpperCase()}
            </div>
          ))}
        </div>
        {newLetter && (
          <div className="text-xs text-gray-300 ml-1">
            <span className="text-[#de0046] font-bold">+{newLetter.toUpperCase()}</span>
          </div>
        )}
      </div>
    );
  };

  const level1NewLetterIndex = -1; // No highlight on first level
  const level2NewLetterIndex = findNewLetterIndex(exampleWords[0], exampleWords[1]);
  const level3NewLetterIndex = findNewLetterIndex(exampleWords[1], exampleWords[2]);
  const level4NewLetterIndex = findNewLetterIndex(exampleWords[2], exampleWords[3]);
  
  const level2NewLetter = getNewLetter(exampleWords[0], exampleWords[1]);
  const level3NewLetter = getNewLetter(exampleWords[1], exampleWords[2]);
  const level4NewLetter = getNewLetter(exampleWords[2], exampleWords[3]);

  return (
    <div>
      <LevelExample 
        level={1} 
        word={exampleWords[0]}
        newLetterIndex={level1NewLetterIndex}
      />
      <LevelExample 
        level={2} 
        word={exampleWords[1]}
        newLetterIndex={level2NewLetterIndex}
        newLetter={level2NewLetter}
      />
      <LevelExample 
        level={3} 
        word={exampleWords[2]}
        newLetterIndex={level3NewLetterIndex}
        newLetter={level3NewLetter}
      />
      <LevelExample 
        level={4} 
        word={exampleWords[3]}
        newLetterIndex={level4NewLetterIndex}
        newLetter={level4NewLetter}
      />
    </div>
  );
};
